import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Pane, Flex, SortDirection, TableColumnType, Icon } from '@fullstory/components';
import { ResourceHeader } from '../ResourceHeader';
import {
  ResourceList,
  defaultDisableSorting,
  defaultOrderItems,
  defaultResourceOptions,
} from '../ResourceList';
import { ControlValue, FilterOptionValueType, fullstorySelectors } from '@fullstory/redux';
import { useFsPageWithFlags } from '@fullstory/fs-script-api';
import css from './index.module.scss';
import { assertExhaustive } from '@fullstory/typeutils';
import { DeleteModal } from './DeleteModal';
import { usePagination } from './usePagination';
import { PaginationControls } from '../PaginationControls';
import { intl } from '@fullstory/intl';
import { timestamp } from '@fullstory/proto/google/protobuf';
import { ItemType, Action } from '../../types';
import { useHistory, useLocation } from 'react-router-dom';
import { History, Location } from 'history';
import {
  useValidatedQueryParams,
  PAGE_QUERY_PARAM,
  FILTER_QUERY_PARAM,
  SEARCH_QUERY_PARAM,
} from './NagivationQueryParams';
import { debounce, isEqual } from 'lodash';
import { useActiveSpaceContext } from '../../contexts';

export * from './NagivationQueryParams';
export * from './usePagination';

export type LibraryObjectResourceType =
  | 'Segment'
  | 'Metric'
  | 'Journey'
  | 'Heatmap'
  | 'Funnel'
  | 'Retention'
  | 'Dashboard'
  | 'Space';

const { getUserInfo } = fullstorySelectors;

type GetFilterProperties<T> = (obj: T) => {
  archived: boolean;
  starred: boolean;
  id: string;
  name: string;
  createdBy: string;
  lastViewed: timestamp | null;
};

type ActionHandler = (id: string) => void;
interface LibraryObjectLandingProps<T extends ItemType> {
  helpText: string;
  icon: React.ComponentPropsWithoutRef<typeof Icon>['name'];
  resourceType: LibraryObjectResourceType;
  description: string;
  testId?: string;
  items: T[];
  isLoading: boolean;
  hasError: boolean;
  onCreate: () => void;
  onRetry: () => void;
  getFilterProperties: GetFilterProperties<T>;
  onView: ActionHandler;
  onDuplicate: ActionHandler;
  onStar: ActionHandler;
  onArchive?: ActionHandler;
  onDelete?: ActionHandler;
  filter?: FilterOptionValueType;
  search?: string;
  middleColumns?: TableColumnType<T>[];
  sortFunction?: (rows: T[], key: string, direction: SortDirection) => T[];
  templates?: React.ReactNode;
}

const getFilterOptions = (showArchived: boolean) =>
  defaultResourceOptions.filter(option => option.value !== 'archived' || showArchived);

const getActions = (showArchive: boolean, showDelete: boolean) => {
  const defaultActions = ['view', 'duplicate', 'star'];
  if (showArchive) defaultActions.push('archive');
  if (showDelete) defaultActions.push('delete');
  return defaultActions;
};

function filterFn<T>(
  { filter, search }: ControlValue,
  email: string,
  getFilterProperties: GetFilterProperties<T>,
) {
  return (libraryObject: T): boolean => {
    const { archived, createdBy, id, name, starred, lastViewed } =
      getFilterProperties(libraryObject);

    if (archived == null || createdBy == null || name == null) return false;

    return (
      ((filter === 'starred' && starred && !archived) ||
        (filter === 'recent' && !archived && lastViewed) ||
        (filter === 'active' && !archived) ||
        (filter === 'mine' && createdBy === email && !archived) ||
        (filter === 'archived' && archived)) &&
      [name, id, createdBy].some(s => s.toLowerCase().includes(search.toLowerCase()))
    );
  };
}

const libraryObjectLandingMessages: {
  [key in LibraryObjectResourceType]: {
    title: string;
    subtitle: string;
    newButtonText: string;
    findPlaceholder: string;
  };
} = {
  Dashboard: {
    title: intl.formatMessage({ defaultMessage: 'Dashboards', id: 'nwqt7T' }),
    subtitle: intl.formatMessage({ defaultMessage: "What's a Dashboard?", id: 'wI34b+' }),
    newButtonText: intl.formatMessage({ defaultMessage: 'New Dashboard', id: '0KgyFk' }),
    findPlaceholder: intl.formatMessage({ defaultMessage: 'Find Dashboard', id: 'jgIo5W' }),
  },
  Segment: {
    title: intl.formatMessage({ defaultMessage: 'Segments', id: 'VF3DcR' }),
    subtitle: intl.formatMessage({ defaultMessage: "What's a Segment?", id: '55yJ17' }),
    newButtonText: intl.formatMessage({ defaultMessage: 'New Segment', id: '3KxjJR' }),
    findPlaceholder: intl.formatMessage({ defaultMessage: 'Find Segment', id: 'NYEbJF' }),
  },
  Metric: {
    title: intl.formatMessage({ defaultMessage: 'Metrics', id: 'HNBpJ4' }),
    subtitle: intl.formatMessage({ defaultMessage: "What's a Metric?", id: 'wqHNC2' }),
    newButtonText: intl.formatMessage({ defaultMessage: 'New Metric', id: 'cV0tNK' }),
    findPlaceholder: intl.formatMessage({ defaultMessage: 'Find Metric', id: 'mzkKLv' }),
  },
  Funnel: {
    title: intl.formatMessage({ defaultMessage: 'Funnels', id: 'puhcpn' }),
    subtitle: intl.formatMessage({ defaultMessage: "What's a Funnel?", id: 'PgrkY4' }),
    newButtonText: intl.formatMessage({ defaultMessage: 'New Funnel', id: 'uaJSG5' }),
    findPlaceholder: intl.formatMessage({ defaultMessage: 'Find Funnel', id: 'eGz5BS' }),
  },
  Heatmap: {
    title: intl.formatMessage({ defaultMessage: 'Heatmaps', id: 'geWuHd' }),
    subtitle: intl.formatMessage({ defaultMessage: "What's a Heatmap?", id: '3IN4d8' }),
    newButtonText: intl.formatMessage({ defaultMessage: 'New Heatmap', id: 'jtf4Hm' }),
    findPlaceholder: intl.formatMessage({ defaultMessage: 'Find Heatmap', id: 's63A0u' }),
  },
  Journey: {
    title: intl.formatMessage({ defaultMessage: 'Journeys', id: '08hqOd' }),
    subtitle: intl.formatMessage({ defaultMessage: "What's a Journey?", id: 'Oc6sDm' }),
    newButtonText: intl.formatMessage({ defaultMessage: 'New Journey', id: 'hwIbW8' }),
    findPlaceholder: intl.formatMessage({ defaultMessage: 'Find Journey', id: 'hwHlz2' }),
  },
  Retention: {
    title: intl.formatMessage({ defaultMessage: 'Retention', id: 'nL6Jl0' }),
    subtitle: intl.formatMessage({ defaultMessage: "What's Retention?", id: 'U4Q39Z' }),
    newButtonText: intl.formatMessage({ defaultMessage: 'New Retention', id: 'neo+23' }),
    findPlaceholder: intl.formatMessage({ defaultMessage: 'Find Retention', id: 'fxreLc' }),
  },
  Space: {
    title: intl.formatMessage({ defaultMessage: 'Space', id: '0ah2ud' }),
    subtitle: intl.formatMessage({ defaultMessage: "What's a Space?", id: 'sBJp/P' }),
    newButtonText: intl.formatMessage({ defaultMessage: 'New Space', id: 't5xj3B' }),
    findPlaceholder: intl.formatMessage({ defaultMessage: 'Find Space', id: 'Ij24Vt' }),
  },
};

const DEBOUNCE_TIME = 250;
const debouncedUpdateSearchPath = debounce(
  (newValue: ControlValue, history: History<any>, location: Location) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set(FILTER_QUERY_PARAM, newValue.filter);
    queryParams.set(SEARCH_QUERY_PARAM, newValue.search);

    if (!newValue.search) {
      queryParams.delete(SEARCH_QUERY_PARAM);
    }
    queryParams.delete(PAGE_QUERY_PARAM);
    history.replace({ pathname: location.pathname, search: `?${queryParams.toString()}` });
  },
  DEBOUNCE_TIME,
);

export function LibraryObjectLanding<T extends ItemType>({
  onCreate,
  onRetry,
  helpText,
  items,
  isLoading,
  hasError,
  icon,
  resourceType,
  description,
  testId,
  getFilterProperties,
  onView,
  onDuplicate,
  onStar,
  onArchive,
  onDelete,
  middleColumns,
  sortFunction,
  templates,
}: LibraryObjectLandingProps<T>) {
  // clear the active space so that the return to space button doesn't show up
  const { setActiveSpace } = useActiveSpaceContext();
  setActiveSpace();

  const location = useLocation();
  const history = useHistory();
  const { filterBy, search, sort } = useValidatedQueryParams({});
  const [controlValue, setControlValue] = useState<ControlValue>({ filter: filterBy, search });

  useEffect(() => {
    setControlValue(prev => {
      const updatedValue = { filter: filterBy, search };
      return isEqual(prev, updatedValue) ? prev : updatedValue;
    });
  }, [filterBy, search]);

  const onControlValueChange = useCallback(
    (value: ControlValue) => {
      const newValue = { ...value };
      if (newValue.filter != controlValue.filter) {
        newValue.search = '';
      }
      setControlValue(newValue);
      debouncedUpdateSearchPath(newValue, history, location);
    },
    [controlValue.filter, history, location],
  );

  useFsPageWithFlags();
  const { email } = useSelector(getUserInfo);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const isEmpty = items.length === 0;
  const actions = getActions(!!onArchive, !!onDelete);

  const filteredItems = useMemo(
    () =>
      items
        .filter(filterFn(controlValue, email, getFilterProperties))
        .map(item => ({ ...item, actions })),
    [items, controlValue, email, getFilterProperties, actions],
  );

  const {
    page,
    nextPageLocation,
    prevPageLocation,
    disableNext,
    disablePrev,
    start,
    end,
    total,
    getPagedData,
  } = usePagination({ total: filteredItems.length, pageSize: 10 });

  const onItemAction = ({ id, action }: { id: string; action: Action }) => {
    switch (action) {
      case 'view':
        onView(id);
        break;
      case 'duplicate':
        onDuplicate(id);
        break;
      case 'star':
        onStar(id);
        break;
      case 'archive':
        onArchive?.(id);
        break;
      case 'delete':
        setDeleteId(id);
        break;
      default:
        assertExhaustive(action);
    }
  };

  const messages = libraryObjectLandingMessages[resourceType];
  return (
    <Box
      data-component="LibraryObjectLanding"
      className={css.container}
      maxWidth={1000}
      padding={16}
      testId={testId || `${resourceType.toLowerCase()}-landing`}
    >
      <ResourceHeader
        title={messages.title}
        subTitle={messages.subtitle}
        helpText={helpText}
        buttonText={messages.newButtonText}
        onCreate={onCreate}
      />
      {templates}
      <Flex
        column
        alignItems="stretch"
        justifyContent="space-between"
        className={css.tableContainer}
      >
        <Pane
          overflow="hidden"
          borderLeft="standard"
          borderRight="standard"
          borderTop="standard"
          borderBottom={isLoading || filteredItems.length == 0 ? 'standard' : undefined}
          borderRadius={4}
          backgroundColor="default"
        >
          <ResourceList
            items={filteredItems}
            disabled={controlValue.filter === 'archived'}
            controlValue={controlValue}
            onChangeControlValue={onControlValueChange}
            searchPlaceholder={messages.findPlaceholder}
            itemIcon={icon}
            isLoading={isLoading}
            filters={getFilterOptions(!!onArchive)}
            orderItems={defaultOrderItems}
            disableSorting={defaultDisableSorting}
            errorState={
              hasError ? (
                <ResourceList.ErrorState resourceType={resourceType} onRetry={onRetry} />
              ) : undefined
            }
            emptyState={
              isEmpty ? (
                <ResourceList.EmptyState
                  resourceType={resourceType}
                  onCreate={onCreate}
                  icon={icon}
                  description={description}
                />
              ) : undefined
            }
            noMatchState={
              !isEmpty && !filteredItems.length ? (
                <ResourceList.NoMatchState resourceType={resourceType} />
              ) : undefined
            }
            onItemAction={onItemAction}
            getPagedData={getPagedData}
            middleColumns={middleColumns}
            sortFunction={sortFunction}
            defaultSort={sort}
            shouldSetSortParams
          />
        </Pane>
        {/* only render pagination if we have more than one page */}
        {(!disableNext || !disablePrev) && (
          <Box padding={24} testId="pager" width="100%">
            <PaginationControls
              page={page}
              nextPageLocation={nextPageLocation}
              prevPageLocation={prevPageLocation}
              disableNext={disableNext}
              disablePrev={disablePrev}
              start={start}
              end={end}
              total={total}
              loading={isLoading}
              resourceType={resourceType}
            />
          </Box>
        )}
      </Flex>
      {deleteId && onDelete && (
        <DeleteModal
          onCloseSignal={() => setDeleteId(null)}
          onConfirm={() => onDelete(deleteId)}
          items={filteredItems}
          id={deleteId}
          resourceType={resourceType}
        />
      )}
    </Box>
  );
}
